
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TaskForm } from "@/components/tasks/TaskForm";
import { TaskFilters } from "@/components/tasks/TaskFilters";
import { TaskViewToggle } from "@/components/tasks/TaskViewToggle";
import { TaskListView } from "@/components/tasks/TaskListView";
import { TaskTableView } from "@/components/tasks/TaskTableView";
import { TaskKanbanBoard } from "@/components/tasks/TaskKanbanBoard";
import { TaskSortControls } from "@/components/tasks/TaskSortControls";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

const Tasks = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const { currentOrganization, isLoading: orgLoading, organizations } = useOrganization();
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterUser, setFilterUser] = useState<string>("all");
  const [view, setView] = useState<'cards' | 'table' | 'kanban'>('cards');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [showOldCompletedTasks, setShowOldCompletedTasks] = useState(false);
  const [sortBy, setSortBy] = useState<string>("due_date");
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to access this page",
          variant: "destructive",
        });
        navigate("/auth");
      }
    };
    checkUser();
  }, [navigate, toast]);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setUserRole(profile.role);
        }
      }
    };
    fetchUserRole();
  }, []);

  // Debug logging for organization state
  useEffect(() => {
    console.log('📊 Tasks page: Organization state', {
      currentOrganization,
      orgLoading,
      organizationsCount: organizations.length,
      hasOrganizations: organizations.length > 0
    });
  }, [currentOrganization, orgLoading, organizations]);

  const { data: tasksData, isLoading, error } = useQuery({
    queryKey: ["tasks", filterStatus, filterUser, currentOrganization?.id, sortBy, sortOrder],
    queryFn: async () => {
      if (!currentOrganization) throw new Error("No organization selected");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user");

      // Direct database query with filters and sorting
      let query = supabase
        .from("tasks")
        .select(`
          *,
          contacts:contact_id (id, name, email, company),
          projects:project_id (id, name, description),
          leads:lead_id (id, name),
          assignee:profiles!tasks_assigned_to_fkey (id, full_name, email)
        `)
        .eq('organization_id', currentOrganization.id);

      // Apply sorting
      const ascending = sortOrder === 'asc';
      if (sortBy === 'priority') {
        // Custom priority sorting
        query = query.order('priority', { ascending });
      } else {
        query = query.order(sortBy, { ascending, nullsFirst: false });
      }

      if (filterStatus && filterStatus !== "all") {
        query = query.eq('status', filterStatus);
      }

      if (filterUser && filterUser !== "all") {
        if (filterUser === "unassigned") {
          query = query.is('assigned_to', null);
        } else {
          query = query.eq('assigned_to', filterUser);
        }
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message || "Failed to fetch tasks");
      }

      // Post-process data to ensure nested objects have required properties
      const processedTasks = data?.map(task => ({
        ...task,
        contacts: task.contacts || { id: '', name: '', email: '', company: '' },
        projects: task.projects || { id: '', name: '', description: '' },
        leads: task.leads || { id: '', name: '' },
        assignee: task.assignee || { id: '', full_name: '', email: '' }
      })) || [];

      return processedTasks;
    },
    enabled: !!currentOrganization,
  });

  const handleTaskComplete = async (taskId: string) => {
    try {
      if (!currentOrganization?.id) {
        throw new Error("No organization selected");
      }

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: "update",
          taskId: taskId,
          task: { status: 'completed' },
          organizationId: currentOrganization.id
        }
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      
      toast({
        title: "Task completed",
        description: "The task has been marked as completed",
      });
    } catch (error) {
      console.error('Error completing task:', error);
      toast({
        title: "Error",
        description: "Failed to complete task",
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      if (!currentOrganization?.id) {
        throw new Error("No organization selected");
      }

      const { error } = await supabase.functions.invoke("tasks-operations", {
        body: {
          action: "update",
          taskId: taskId,
          task: { status: newStatus },
          organizationId: currentOrganization.id
        }
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      
      toast({
        title: "Status updated",
        description: `Task status changed to ${newStatus.replace('_', ' ')}`,
      });
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
    }
  };

  const handleSortChange = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  // Get tasks array from data
  const tasks = tasksData || [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Tasks</h1>
            {currentOrganization && (
              <p className="text-muted-foreground">
                {currentOrganization.name}
              </p>
            )}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>Create Task</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6">
                <TaskForm 
                  onSuccess={() => {
                    toast({
                      title: "Success",
                      description: "Task created successfully"
                    });
                    setOpen(false);
                  }} 
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-col lg:flex-row justify-between mb-6 gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <TaskFilters
              filterStatus={filterStatus}
              filterUser={filterUser}
              onStatusChange={setFilterStatus}
              onUserChange={setFilterUser}
            />
            <TaskSortControls
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          </div>
          <TaskViewToggle 
            view={view} 
            onViewChange={setView}
            showOldCompletedTasks={showOldCompletedTasks}
            onShowOldCompletedTasksChange={setShowOldCompletedTasks}
          />
        </div>

        {!currentOrganization ? (
          <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/50">
            <h3 className="text-lg font-medium mb-2">No Organization Selected</h3>
            <p className="text-muted-foreground mb-4">Please select an organization to view tasks.</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-destructive/10">
            <h3 className="text-lg font-medium mb-2">Error Loading Tasks</h3>
            <p className="text-muted-foreground mb-4">{(error as Error).message}</p>
            <Button onClick={() => queryClient.invalidateQueries({ queryKey: ["tasks"] })}>
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-muted-foreground">Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/50">
            <h3 className="text-lg font-medium mb-2">No Tasks Found</h3>
            <p className="text-muted-foreground">
              {filterStatus !== "all" || filterUser !== "all" 
                ? "Try changing your filters to see more tasks." 
                : "Create a new task to get started."}
            </p>
          </div>
        ) : (
          view === 'cards' ? (
            <TaskListView 
              tasks={tasks} 
              userRole={userRole} 
              onTaskComplete={handleTaskComplete}
              onStatusChange={handleStatusChange}
              showOldCompletedTasks={showOldCompletedTasks}
            />
          ) : view === 'table' ? (
            <TaskTableView 
              tasks={tasks} 
              userRole={userRole}
              onTaskComplete={handleTaskComplete}
              onStatusChange={handleStatusChange}
              showOldCompletedTasks={showOldCompletedTasks}
            />
          ) : (
            <TaskKanbanBoard 
              tasks={tasks} 
              userRole={userRole}
              onTaskComplete={handleTaskComplete}
              showOldCompletedTasks={showOldCompletedTasks}
            />
          )
        )}
      </div>
    </div>
  );
};

export default Tasks;
